import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Crown, Wallet, CheckCircle2, XCircle, Search, Plus, Minus, Flame, Sparkles, Radio, FileText, Trash2, Edit, Send, Upload, Music, Video, X, Link2, User, MessageSquare, Briefcase, Calendar, BarChart3, BookOpen, Shield } from 'lucide-react';
import AdminScriptures from '@/components/admin/AdminScriptures';
import AdminSafetyDashboard from '@/components/admin/AdminSafetyDashboard';
import AdminESignatures from '@/components/admin/AdminESignatures';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get active tab from URL path
const getTabFromPath = (pathname) => {
  if (pathname.includes('/admin/users')) return 'users';
  if (pathname.includes('/admin/coaches')) return 'coaches';
  if (pathname.includes('/admin/group-sessions')) return 'group-sessions';
  if (pathname.includes('/admin/requests')) return 'requests';
  if (pathname.includes('/admin/coach-notes')) return 'coach-notes';
  if (pathname.includes('/admin/invoices')) return 'coach-invoices';
  if (pathname.includes('/admin/messages')) return 'messages';
  if (pathname.includes('/admin/support')) return 'support';
  if (pathname.includes('/admin/content')) return 'content';
  if (pathname.includes('/admin/communities')) return 'communities';
  if (pathname.includes('/admin/safety')) return 'safety';
  if (pathname.includes('/admin/esign')) return 'esign';
  return 'dashboard';
};

const AdminPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const audioInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [hotSeats, setHotSeats] = useState([]);
  const [concierge, setConcierge] = useState([]);
  const [transmissions, setTransmissions] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [coachNotes, setCoachNotes] = useState([]);
  const [coachInvoices, setCoachInvoices] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [groupSessions, setGroupSessions] = useState([]);
  const [adminStats, setAdminStats] = useState({ totalClients: 0, totalCoaches: 0, clientsByProgram: {} });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('');
  const [invoiceSortBy, setInvoiceSortBy] = useState('date'); // 'date', 'status', 'due', 'amount'
  const [notesSortBy, setNotesSortBy] = useState('date'); // 'date', 'coach', 'client'
  const [notesSearchTerm, setNotesSearchTerm] = useState('');
  const [notesCoachFilter, setNotesCoachFilter] = useState('');
  const [notesClientFilter, setNotesClientFilter] = useState('');

  // Messaging state
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  // Coaches management state
  const [showCoachDialog, setShowCoachDialog] = useState(false);
  const [editingCoach, setEditingCoach] = useState(null);
  const [coachForm, setCoachForm] = useState({
    name: '', title: '', short_bio: '', bio: '', image_url: '',
    credentials: '', specialties: '', calendly_link: '',
    inner_edge_eligible: false, is_black_female: false
  });

  // Group sessions state
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [sessionForm, setSessionForm] = useState({
    title: '', description: '', session_type: 'power_and_presence',
    scheduled_date: '', scheduled_time: '', duration_minutes: 60,
    zoom_link: 'https://us06web.zoom.us/my/crystalanddrnicole',
    zoom_password: '1111', assigned_tiers: [], facilitators: 'Crystal Khalil, Dr. Nicole LaBeach'
  });

  // Grant/Revoke Modal State
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTier, setSelectedTier] = useState('');
  const [walletAdjustment, setWalletAdjustment] = useState({ type: '', amount: 0, reason: '' });

  // User Management State
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [showUserActionsDialog, setShowUserActionsDialog] = useState(false);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [userActivity, setUserActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [selectedUserForAction, setSelectedUserForAction] = useState(null);
  const [createUserForm, setCreateUserForm] = useState({
    email: '', full_name: '', password: '', role: 'client', active_tiers: [], send_welcome_email: true
  });
  const [suspendReason, setSuspendReason] = useState('');

  // Community Management State
  const [adminCommunities, setAdminCommunities] = useState([]);
  const [editingCommunity, setEditingCommunity] = useState(null);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [selectedCommunityForPosts, setSelectedCommunityForPosts] = useState(null);

  // Content Management State
  const [newTransmission, setNewTransmission] = useState({ title: '', content: '', audio_url: '', video_url: '', vimeo_url: '', youtube_url: '', media_type: null });
  const [newPrompt, setNewPrompt] = useState({ title: '', prompt_text: '' });
  const [showTransmissionForm, setShowTransmissionForm] = useState(false);
  const [showPromptForm, setShowPromptForm] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchData();
    fetchAdminStats();
    fetchAllUsers();
    fetchCoaches();
    fetchGroupSessions();
  }, [searchTerm, tierFilter, invoiceStatusFilter]);

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // Fetch messages when on messages tab
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchAdminConversations();
    }
    if (activeTab === 'communities') {
      fetchAdminCommunities();
    }
  }, [activeTab]);

  const fetchAdminStats = async () => {
    try {
      const usersRes = await axios.get(`${API_URL}/api/admin/users`);
      const allUsersData = usersRes.data || [];
      
      // Calculate stats
      const clients = allUsersData.filter(u => u.role !== 'admin');
      const coachUsers = allUsersData.filter(u => u.is_coach);
      
      // Count by program/tier
      const clientsByProgram = {
        inner_circle: allUsersData.filter(u => u.activeTiers?.includes('inner_circle')).length,
        inner_edge: allUsersData.filter(u => u.activeTiers?.includes('inner_edge')).length,
        coaching_activation: allUsersData.filter(u => u.activeTiers?.includes('coaching_activation')).length,
        sdc: allUsersData.filter(u => u.activeTiers?.includes('sdc')).length,
        costa_rica_retreat: allUsersData.filter(u => u.activeTiers?.includes('costa_rica_retreat')).length
      };
      
      setAdminStats({
        totalClients: clients.length,
        totalCoaches: coachUsers.length,
        clientsByProgram
      });
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`);
      setAllUsers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch all users:', error);
    }
  };

  const fetchCoaches = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/coaches`);
      setCoaches(res.data || []);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    }
  };

  const fetchGroupSessions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/group-sessions`);
      setGroupSessions(res.data || []);
    } catch (error) {
      console.error('Failed to fetch group sessions:', error);
    }
  };

  const fetchAdminConversations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/messages`);
      setConversations(res.data || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };

  const fetchAdminCommunities = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/communities`);
      setAdminCommunities(res.data || []);
    } catch (error) {
      console.error('Failed to fetch communities:', error);
    }
  };

  const fetchCommunityPosts = async (communityId) => {
    try {
      const res = await axios.get(`${API_URL}/api/communities/${communityId}/posts`);
      setCommunityPosts(res.data || []);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  };

  const handleUpdateCommunity = async (communityId, updateData) => {
    try {
      await axios.put(`${API_URL}/api/admin/communities/${communityId}`, updateData);
      toast.success('Community updated successfully');
      setEditingCommunity(null);
      fetchAdminCommunities();
    } catch (error) {
      toast.error('Failed to update community');
    }
  };

  const handleDeletePost = async (communityId, postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/communities/${communityId}/posts/${postId}`);
      toast.success('Post deleted');
      fetchCommunityPosts(communityId);
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const fetchMessagesWithUser = async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/messages/${userId}`);
      setMessages(res.data || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  // Coach management functions
  const handleSaveCoach = async () => {
    try {
      const coachData = {
        ...coachForm,
        credentials: coachForm.credentials.split(',').map(c => c.trim()).filter(Boolean),
        specialties: coachForm.specialties.split(',').map(s => s.trim()).filter(Boolean)
      };

      if (editingCoach) {
        await axios.put(`${API_URL}/api/admin/coaches/${editingCoach.id}`, coachData);
        toast.success('Coach updated');
      } else {
        await axios.post(`${API_URL}/api/admin/coaches`, coachData);
        toast.success('Coach added');
      }
      setShowCoachDialog(false);
      setEditingCoach(null);
      setCoachForm({ name: '', title: '', short_bio: '', bio: '', image_url: '', credentials: '', specialties: '', calendly_link: '', inner_edge_eligible: false, is_black_female: false });
      fetchCoaches();
    } catch (error) {
      toast.error('Failed to save coach');
    }
  };

  const handleEditCoach = (coach) => {
    setEditingCoach(coach);
    setCoachForm({
      name: coach.name || '',
      title: coach.title || '',
      short_bio: coach.short_bio || '',
      bio: coach.bio || '',
      image_url: coach.image_url || '',
      credentials: (coach.credentials || []).join(', '),
      specialties: (coach.specialties || []).join(', '),
      calendly_link: coach.calendly_link || '',
      inner_edge_eligible: coach.inner_edge_eligible || false,
      is_black_female: coach.is_black_female || false
    });
    setShowCoachDialog(true);
  };

  const handleDeleteCoach = async (coachId) => {
    if (!window.confirm('Are you sure you want to delete this coach?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/coaches/${coachId}`);
      toast.success('Coach deleted');
      fetchCoaches();
    } catch (error) {
      toast.error('Failed to delete coach');
    }
  };

  // Group session functions
  const handleSaveSession = async () => {
    try {
      const sessionData = {
        ...sessionForm,
        facilitators: sessionForm.facilitators.split(',').map(f => f.trim()).filter(Boolean)
      };

      if (editingSession) {
        await axios.put(`${API_URL}/api/admin/group-sessions/${editingSession.id}`, sessionData);
        toast.success('Session updated');
      } else {
        await axios.post(`${API_URL}/api/admin/group-sessions`, sessionData);
        toast.success('Session created');
      }
      setShowSessionDialog(false);
      setEditingSession(null);
      setSessionForm({ title: '', description: '', session_type: 'power_and_presence', scheduled_date: '', scheduled_time: '', duration_minutes: 60, zoom_link: 'https://us06web.zoom.us/my/crystalanddrnicole', zoom_password: '1111', assigned_tiers: [], facilitators: 'Crystal Khalil, Dr. Nicole LaBeach' });
      fetchGroupSessions();
    } catch (error) {
      toast.error('Failed to save session');
    }
  };

  const handleMarkSessionComplete = async (sessionId) => {
    try {
      await axios.put(`${API_URL}/api/admin/group-sessions/${sessionId}`, { status: 'completed' });
      toast.success('Session marked complete');
      fetchGroupSessions();
    } catch (error) {
      toast.error('Failed to update session');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/group-sessions/${sessionId}`);
      toast.success('Session deleted');
      fetchGroupSessions();
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const handleSendAdminMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedPartner) return;

    setSendingMessage(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/messages`, {
        receiver_id: selectedPartner.id,
        content: newMessage.trim()
      });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      fetchAdminConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const fetchData = async () => {
    try {
      const [usersRes, bookingsRes, hotSeatsRes, conciergeRes, transmissionsRes, promptsRes, notesRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, { params: { search: searchTerm, tier: tierFilter || undefined } }),
        axios.get(`${API_URL}/api/admin/bookings`),
        axios.get(`${API_URL}/api/admin/requests/hot-seat`),
        axios.get(`${API_URL}/api/admin/requests/concierge`),
        axios.get(`${API_URL}/api/admin/transmissions`),
        axios.get(`${API_URL}/api/admin/prompts`),
        axios.get(`${API_URL}/api/admin/coach-notes`),
        axios.get(`${API_URL}/api/admin/coach-invoices`, { params: { status: invoiceStatusFilter || undefined } })
      ]);
      
      setUsers(usersRes.data);
      setBookings(bookingsRes.data);
      setHotSeats(hotSeatsRes.data);
      setConcierge(conciergeRes.data);
      setTransmissions(transmissionsRes.data);
      setPrompts(promptsRes.data);
      setCoachNotes(notesRes.data);
      setCoachInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantTier = async (userId) => {
    if (!selectedTier) {
      toast.error('Please select a tier');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/admin/tiers/grant`, {
        user_id: userId,
        tier: selectedTier,
        granted_by: user.id,
        notes: 'Granted via admin panel'
      });
      toast.success(`Granted ${selectedTier} to user`);
      setSelectedTier('');
      fetchData();
    } catch (error) {
      toast.error('Failed to grant tier');
    }
  };

  const handleRevokeTier = async (userId, tier) => {
    if (!confirm(`Are you sure you want to revoke ${tier}?`)) return;
    
    try {
      await axios.post(`${API_URL}/api/admin/tiers/revoke`, {
        user_id: userId,
        tier: tier,
        granted_by: user.id,
        notes: 'Revoked via admin panel'
      });
      toast.success(`Revoked ${tier}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke tier');
    }
  };

  const handleWalletAdjust = async (userId) => {
    if (!walletAdjustment.type || !walletAdjustment.amount || !walletAdjustment.reason) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/admin/wallet/adjust`, {
        user_id: userId,
        wallet_type: walletAdjustment.type,
        amount: parseInt(walletAdjustment.amount),
        reason: walletAdjustment.reason
      });
      toast.success('Wallet adjusted');
      setWalletAdjustment({ type: '', amount: 0, reason: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to adjust wallet');
    }
  };

  // ========== USER MANAGEMENT FUNCTIONS ==========
  
  const handleCreateUser = async () => {
    if (!createUserForm.email || !createUserForm.full_name) {
      toast.error('Email and name are required');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/create`, createUserForm);
      toast.success(`User created! ${response.data.temp_password ? `Temp password: ${response.data.temp_password}` : ''}`);
      setShowCreateUserDialog(false);
      setCreateUserForm({ email: '', full_name: '', password: '', role: 'client', active_tiers: [], send_welcome_email: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleSuspendUser = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/suspend?reason=${encodeURIComponent(suspendReason)}`);
      toast.success('User suspended');
      setShowUserActionsDialog(false);
      setSuspendReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/reactivate`);
      toast.success('User reactivated');
      setShowUserActionsDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate user');
    }
  };

  const handleDeleteUser = async (userId, permanent = false) => {
    const confirmMsg = permanent 
      ? 'This will PERMANENTLY delete this user and ALL their data. This cannot be undone. Are you sure?'
      : 'This will soft-delete the user (they can be restored later). Continue?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}?permanent=${permanent}`);
      toast.success(permanent ? 'User permanently deleted' : 'User terminated');
      setShowUserActionsDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('This will reset the user\'s password and send them a new one. Continue?')) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/${userId}/reset-password`);
      toast.success(`Password reset! New password: ${response.data.new_password}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const fetchUserActivity = async (userId) => {
    setLoadingActivity(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}/activity`);
      setUserActivity(response.data);
      setShowActivityDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch user activity');
    } finally {
      setLoadingActivity(false);
    }
  };

  const getActivityIcon = (iconName) => {
    const icons = {
      'shield': Shield,
      'message-square': MessageSquare,
      'book-open': BookOpen,
      'calendar': Calendar,
      'mail': MessageSquare,
      'award': Crown,
      'gem': Sparkles,
      'target': BarChart3,
      'user-plus': User
    };
    const Icon = icons[iconName] || User;
    return <Icon className="h-4 w-4" />;
  };

  const getActivityColor = (type) => {
    const colors = {
      'admin_action': 'text-purple-600 bg-purple-100',
      'post': 'text-blue-600 bg-blue-100',
      'journal': 'text-green-600 bg-green-100',
      'booking': 'text-terracotta bg-orange-100',
      'messages': 'text-indigo-600 bg-indigo-100',
      'tier_change': 'text-amber-600 bg-amber-100',
      'diamond_points': 'text-pink-600 bg-pink-100',
      'focus_path': 'text-teal-600 bg-teal-100',
      'account': 'text-forest bg-green-100'
    };
    return colors[type] || 'text-gray-600 bg-gray-100';
  };

  const handleCompleteSession = async (bookingId) => {
    try {
      await axios.post(`${API_URL}/api/admin/session/complete?booking_id=${bookingId}`);
      toast.success('Session marked complete, wallet decremented');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete session');
    }
  };

  const handleUpdateRequest = async (type, requestId, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/requests/${type}/${requestId}?status=${status}`);
      toast.success('Request updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  // Invoice management functions
  const handleUpdateInvoice = async (invoiceId, updates) => {
    try {
      await axios.put(`${API_URL}/api/admin/invoices/${invoiceId}`, updates);
      toast.success('Invoice updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  // Content Management Functions
  // Handle media file upload
  const handleMediaUpload = async (event, mediaType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (100MB max)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 100MB.');
      return;
    }

    setUploadingMedia(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/admin/upload-media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      const { url, media_type } = response.data;
      
      if (media_type === 'audio') {
        setNewTransmission(prev => ({ ...prev, audio_url: url, video_url: '', media_type: 'audio' }));
      } else {
        setNewTransmission(prev => ({ ...prev, video_url: url, audio_url: '', media_type: 'video' }));
      }
      
      toast.success(`${media_type === 'audio' ? 'Audio' : 'Video'} uploaded successfully!`);
    } catch (error) {
      toast.error('Failed to upload file. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setUploadingMedia(false);
      setUploadProgress(0);
    }
  };

  const clearMedia = () => {
    setNewTransmission(prev => ({ ...prev, audio_url: '', video_url: '', vimeo_url: '', youtube_url: '', media_type: null }));
  };

  // Extract Vimeo video ID from URL
  const extractVimeoId = (url) => {
    if (!url) return null;
    const patterns = [
      /vimeo\.com\/(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/,
      /vimeo\.com\/video\/(\d+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Extract YouTube video ID from URL
  const extractYoutubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /youtube\.com\/watch\?v=([^&]+)/,
      /youtu\.be\/([^?]+)/,
      /youtube\.com\/embed\/([^?]+)/,
      /youtube\.com\/v\/([^?]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleVimeoUrlChange = (url) => {
    const vimeoId = extractVimeoId(url);
    if (vimeoId || url === '') {
      setNewTransmission(prev => ({ 
        ...prev, 
        vimeo_url: url,
        youtube_url: '',
        audio_url: '',
        video_url: '',
        media_type: url ? 'vimeo' : null 
      }));
    } else if (url) {
      toast.error('Please enter a valid Vimeo URL (e.g., https://vimeo.com/123456789)');
    }
  };

  const handleYoutubeUrlChange = (url) => {
    const youtubeId = extractYoutubeId(url);
    if (youtubeId || url === '') {
      setNewTransmission(prev => ({ 
        ...prev, 
        youtube_url: url,
        vimeo_url: '',
        audio_url: '',
        video_url: '',
        media_type: url ? 'youtube' : null 
      }));
    } else if (url) {
      toast.error('Please enter a valid YouTube URL (e.g., https://youtube.com/watch?v=...)');
    }
  };

  const handleCreateTransmission = async () => {
    if (!newTransmission.title || !newTransmission.content) {
      toast.error('Please fill in title and content');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/transmission`, {
        title: newTransmission.title,
        content: newTransmission.content,
        audio_url: newTransmission.audio_url || null,
        video_url: newTransmission.video_url || null,
        vimeo_url: newTransmission.vimeo_url || null,
        youtube_url: newTransmission.youtube_url || null,
        media_type: newTransmission.media_type || null,
        is_published: true
      });
      toast.success('Gem published successfully!');
      setNewTransmission({ title: '', content: '', audio_url: '', video_url: '', vimeo_url: '', youtube_url: '', media_type: null });
      setShowTransmissionForm(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create transmission');
    }
  };

  const handleDeleteTransmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transmission?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/transmission/${id}`);
      toast.success('Gem deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete transmission');
    }
  };

  const handleCreatePrompt = async () => {
    if (!newPrompt.title || !newPrompt.prompt_text) {
      toast.error('Please fill in title and prompt text');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/prompt`, {
        title: newPrompt.title,
        prompt_text: newPrompt.prompt_text,
        is_published: true
      });
      toast.success('Reflection prompt published successfully!');
      setNewPrompt({ title: '', prompt_text: '' });
      setShowPromptForm(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create prompt');
    }
  };

  const handleDeletePrompt = async (id) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/prompt/${id}`);
      toast.success('Prompt deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete prompt');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate days until due (NET 60 from creation)
  const getInvoiceDueInfo = (createdAt) => {
    const created = new Date(createdAt);
    const due = new Date(created.getTime() + (60 * 24 * 60 * 60 * 1000)); // NET 60
    const today = new Date();
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return {
      dueDate: due,
      daysUntilDue: diffDays,
      isOverdue: diffDays < 0
    };
  };

  // Sort invoices based on selected sort option
  const sortInvoices = (invoices) => {
    const statusPriority = { pending: 1, approved: 2, returned: 3, paid: 4, rescinded: 5 };
    
    return [...invoices].sort((a, b) => {
      switch (invoiceSortBy) {
        case 'status':
          return (statusPriority[a.status] || 99) - (statusPriority[b.status] || 99);
        case 'due':
          // Sort by days until due (overdue first, then soonest due)
          const dueA = getInvoiceDueInfo(a.created_at);
          const dueB = getInvoiceDueInfo(b.created_at);
          return dueA.daysUntilDue - dueB.daysUntilDue;
        case 'amount':
          return (b.amount || 0) - (a.amount || 0); // Highest amount first
        case 'date':
        default:
          return new Date(b.created_at) - new Date(a.created_at); // Newest first
      }
    });
  };

  const tierBadgeColors = {
    inner_circle: 'bg-terracotta',
    inner_edge: 'bg-forest',
    costa_rica: 'bg-terracotta',
    coaching_activation: 'bg-forest',
    sdc: 'bg-terracotta'
  };

  // Get page info based on active tab
  const getPageInfo = () => {
    switch (activeTab) {
      case 'dashboard':
        return { title: 'Admin Dashboard', subtitle: 'Overview of platform metrics and activity' };
      case 'users':
        return { title: 'User Management', subtitle: 'Manage users, tiers, and wallets' };
      case 'coaches':
        return { title: 'Coach Management', subtitle: 'Add, edit, and manage all coaches' };
      case 'group-sessions':
        return { title: 'Group Sessions', subtitle: 'Schedule and manage group coaching sessions' };
      case 'requests':
        return { title: 'Requests', subtitle: 'Handle hot seat and concierge requests' };
      case 'coach-notes':
        return { title: 'Coach Notes', subtitle: 'View confidential notes from all coaches' };
      case 'coach-invoices':
        return { title: 'Invoice Management', subtitle: 'Review and process coach invoices' };
      case 'messages':
        return { title: 'Messages', subtitle: 'Communicate with any user on the platform' };
      case 'support':
        return { title: 'Technical Support', subtitle: 'System updates and support resources' };
      case 'content':
        return { title: 'Content Management', subtitle: 'Manage transmissions, scriptures, and reflections' };
      case 'communities':
        return { title: 'Community Management', subtitle: 'Manage community rooms, images, and moderate conversations' };
      case 'safety':
        return { title: 'Safety & Moderation', subtitle: 'Review reports, flagged content, and manage user safety' };
      case 'esign':
        return { title: 'E-Signatures', subtitle: 'Send and track coaching agreements' };
      default:
        return { title: 'Admin Panel', subtitle: 'Platform administration' };
    }
  };

  const pageInfo = getPageInfo();

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="admin-page">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-forest">{pageInfo.title}</h1>
          <p className="text-gray-600">{pageInfo.subtitle}</p>
        </div>

        {/* Dashboard View */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="card-base">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-forest/10 flex items-center justify-center">
                      <Users className="h-6 w-6 text-forest" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-forest">{adminStats.totalClients}</p>
                      <p className="text-gray-500">Total Clients</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-terracotta/10 flex items-center justify-center">
                      <Briefcase className="h-6 w-6 text-terracotta" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-forest">{adminStats.totalCoaches}</p>
                      <p className="text-gray-500">Active Coaches</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Wallet className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-forest">{coachInvoices.filter(i => i.status === 'pending').length}</p>
                      <p className="text-gray-500">Pending Invoices</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Clients by Program */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Clients by Coaching Program
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-4 bg-sand/30 rounded-xl text-center">
                    <p className="text-2xl font-bold text-forest">{adminStats.clientsByProgram.inner_circle || 0}</p>
                    <p className="text-sm text-gray-600">Inner Circle</p>
                  </div>
                  <div className="p-4 bg-sand/30 rounded-xl text-center">
                    <p className="text-2xl font-bold text-forest">{adminStats.clientsByProgram.inner_edge || 0}</p>
                    <p className="text-sm text-gray-600">Inner Edge</p>
                  </div>
                  <div className="p-4 bg-sand/30 rounded-xl text-center">
                    <p className="text-2xl font-bold text-forest">{adminStats.clientsByProgram.coaching_activation || 0}</p>
                    <p className="text-sm text-gray-600">Signature Coaching</p>
                  </div>
                  <div className="p-4 bg-sand/30 rounded-xl text-center">
                    <p className="text-2xl font-bold text-forest">{adminStats.clientsByProgram.sdc || 0}</p>
                    <p className="text-sm text-gray-600">SDC</p>
                  </div>
                  <div className="p-4 bg-sand/30 rounded-xl text-center">
                    <p className="text-2xl font-bold text-forest">{adminStats.clientsByProgram.costa_rica_retreat || 0}</p>
                    <p className="text-sm text-gray-600">Costa Rica Retreat</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => navigate('/admin/users')} variant="outline">
                    <Users className="h-4 w-4 mr-2" /> Manage Users
                  </Button>
                  <Button onClick={() => navigate('/admin/coaches')} variant="outline">
                    <Briefcase className="h-4 w-4 mr-2" /> Manage Coaches
                  </Button>
                  <Button onClick={() => navigate('/admin/group-sessions')} variant="outline">
                    <Calendar className="h-4 w-4 mr-2" /> Group Sessions
                  </Button>
                  <Button onClick={() => navigate('/admin/invoices')} variant="outline">
                    <Wallet className="h-4 w-4 mr-2" /> Review Invoices
                  </Button>
                  <Button onClick={() => navigate('/admin/content')} variant="outline">
                    <Radio className="h-4 w-4 mr-2" /> Manage Content
                  </Button>
                  <Button onClick={() => navigate('/admin/messages')} variant="outline">
                    <MessageSquare className="h-4 w-4 mr-2" /> Messages
                  </Button>
                  <Button onClick={() => navigate('/videos')} variant="outline">
                    <Video className="h-4 w-4 mr-2" /> Manage Library
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Coaches Tab */}
        {activeTab === 'coaches' && (
          <Card className="card-base">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Coach Management ({coaches.length} coaches)
                </CardTitle>
                <Button onClick={() => { setEditingCoach(null); setCoachForm({ name: '', title: '', short_bio: '', bio: '', image_url: '', credentials: '', specialties: '', calendly_link: '', inner_edge_eligible: false, is_black_female: false }); setShowCoachDialog(true); }} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" /> Add Coach
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {coaches.map((coach) => (
                  <div key={coach.id} className="p-4 border border-sand rounded-xl">
                    <div className="flex items-start gap-3 mb-3">
                      <img 
                        src={coach.image_url || 'https://via.placeholder.com/60'} 
                        alt={coach.name} 
                        className="w-14 h-14 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="font-medium text-forest">{coach.name}</h3>
                        <p className="text-sm text-gray-500">{coach.title}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{coach.short_bio}</p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {coach.inner_edge_eligible && <Badge className="bg-forest/10 text-forest text-xs">Inner Edge</Badge>}
                      {coach.is_black_female && <Badge className="bg-terracotta/10 text-terracotta text-xs">Black Female</Badge>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleEditCoach(coach)}>
                        <Edit className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteCoach(coach.id)}>
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Coach Edit Dialog */}
        <Dialog open={showCoachDialog} onOpenChange={setShowCoachDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCoach ? 'Edit Coach' : 'Add New Coach'}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={coachForm.name} onChange={(e) => setCoachForm({...coachForm, name: e.target.value})} placeholder="Dr. Jane Smith" />
                </div>
                <div>
                  <Label>Title</Label>
                  <Input value={coachForm.title} onChange={(e) => setCoachForm({...coachForm, title: e.target.value})} placeholder="Executive Coach, PCC" />
                </div>
              </div>
              <div>
                <Label>Short Bio</Label>
                <Input value={coachForm.short_bio} onChange={(e) => setCoachForm({...coachForm, short_bio: e.target.value})} placeholder="Brief description for cards" />
              </div>
              <div>
                <Label>Full Bio</Label>
                <Textarea value={coachForm.bio} onChange={(e) => setCoachForm({...coachForm, bio: e.target.value})} placeholder="Detailed biography" rows={4} />
              </div>
              <div>
                <Label>Photo URL</Label>
                <Input value={coachForm.image_url} onChange={(e) => setCoachForm({...coachForm, image_url: e.target.value})} placeholder="https://..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Credentials (comma-separated)</Label>
                  <Input value={coachForm.credentials} onChange={(e) => setCoachForm({...coachForm, credentials: e.target.value})} placeholder="PCC, MBA, PhD" />
                </div>
                <div>
                  <Label>Specialties (comma-separated)</Label>
                  <Input value={coachForm.specialties} onChange={(e) => setCoachForm({...coachForm, specialties: e.target.value})} placeholder="Leadership, Strategy" />
                </div>
              </div>
              <div>
                <Label>Calendly Link</Label>
                <Input value={coachForm.calendly_link} onChange={(e) => setCoachForm({...coachForm, calendly_link: e.target.value})} placeholder="https://calendly.com/..." />
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={coachForm.inner_edge_eligible} onChange={(e) => setCoachForm({...coachForm, inner_edge_eligible: e.target.checked})} />
                  <span className="text-sm">Inner Edge Eligible</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={coachForm.is_black_female} onChange={(e) => setCoachForm({...coachForm, is_black_female: e.target.checked})} />
                  <span className="text-sm">Black Female Coach</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCoachDialog(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleSaveCoach}>
                {editingCoach ? 'Update Coach' : 'Add Coach'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Group Sessions Tab */}
        {activeTab === 'group-sessions' && (
          <Card className="card-base">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Group Sessions
                </CardTitle>
                <Button onClick={() => { setEditingSession(null); setSessionForm({ title: '', description: '', session_type: 'power_and_presence', scheduled_date: '', scheduled_time: '', duration_minutes: 60, zoom_link: 'https://us06web.zoom.us/my/crystalanddrnicole', zoom_password: '1111', assigned_tiers: [], facilitators: 'Crystal Khalil, Dr. Nicole LaBeach' }); setShowSessionDialog(true); }} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" /> Schedule Session
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {groupSessions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No group sessions scheduled yet.</p>
              ) : (
                <div className="space-y-4">
                  {groupSessions.map((session) => (
                    <div key={session.id} className="p-4 border border-sand rounded-xl">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-medium text-forest">{session.title}</h3>
                            <Badge className={session.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                              {session.status}
                            </Badge>
                            <Badge variant="outline">{session.session_type?.replace(/_/g, ' ')}</Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{session.description}</p>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                            <span><Calendar className="h-4 w-4 inline mr-1" />{session.scheduled_date} at {session.scheduled_time}</span>
                            <span>Duration: {session.duration_minutes} min</span>
                            <span>Facilitators: {session.facilitators?.join(', ')}</span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {session.assigned_tiers?.map(tier => (
                              <Badge key={tier} variant="outline" className="text-xs">{tier.replace(/_/g, ' ')}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {session.status !== 'completed' && (
                            <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleMarkSessionComplete(session.id)}>
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Complete
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteSession(session.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Group Session Dialog */}
        <Dialog open={showSessionDialog} onOpenChange={setShowSessionDialog}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>{editingSession ? 'Edit Session' : 'Schedule New Group Session'}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input value={sessionForm.title} onChange={(e) => setSessionForm({...sessionForm, title: e.target.value})} placeholder="Power & Presence Session 1" />
              </div>
              <div>
                <Label>Session Type</Label>
                <Select value={sessionForm.session_type} onValueChange={(v) => setSessionForm({...sessionForm, session_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="power_and_presence">Power & Presence</SelectItem>
                    <SelectItem value="sdc_group">SDC Group Session</SelectItem>
                    <SelectItem value="inner_circle_group">Inner Circle Group</SelectItem>
                    <SelectItem value="wonder_twin_group">Wonder Twin Group</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={sessionForm.description} onChange={(e) => setSessionForm({...sessionForm, description: e.target.value})} placeholder="Session details..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={sessionForm.scheduled_date} onChange={(e) => setSessionForm({...sessionForm, scheduled_date: e.target.value})} />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Input type="time" value={sessionForm.scheduled_time} onChange={(e) => setSessionForm({...sessionForm, scheduled_time: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Zoom Link</Label>
                  <Input value={sessionForm.zoom_link} onChange={(e) => setSessionForm({...sessionForm, zoom_link: e.target.value})} />
                </div>
                <div>
                  <Label>Zoom Password</Label>
                  <Input value={sessionForm.zoom_password} onChange={(e) => setSessionForm({...sessionForm, zoom_password: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Facilitators (comma-separated)</Label>
                <Input value={sessionForm.facilitators} onChange={(e) => setSessionForm({...sessionForm, facilitators: e.target.value})} />
              </div>
              <div>
                <Label>Assign to Tiers</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['inner_circle', 'inner_edge', 'coaching_activation', 'sdc'].map(tier => (
                    <label key={tier} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-sand/50">
                      <input 
                        type="checkbox" 
                        checked={sessionForm.assigned_tiers.includes(tier)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSessionForm({...sessionForm, assigned_tiers: [...sessionForm.assigned_tiers, tier]});
                          } else {
                            setSessionForm({...sessionForm, assigned_tiers: sessionForm.assigned_tiers.filter(t => t !== tier)});
                          }
                        }}
                      />
                      <span className="text-sm">{tier.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSessionDialog(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleSaveSession}>
                {editingSession ? 'Update Session' : 'Schedule Session'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Technical Support Tab */}
        {activeTab === 'support' && (
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-forest">Technical Support</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-6 bg-blue-50 rounded-xl">
                <h3 className="font-medium text-forest mb-2">System Updates & Support</h3>
                <p className="text-gray-600 mb-4">Need help with system updates, bug fixes, or new feature requests? Contact Emergent support.</p>
                <Button onClick={() => window.open('https://emergent.sh/support', '_blank')} className="btn-primary">
                  <MessageSquare className="h-4 w-4 mr-2" /> Contact Emergent Support
                </Button>
              </div>
              <div className="p-6 bg-sand/30 rounded-xl">
                <h3 className="font-medium text-forest mb-2">Documentation</h3>
                <p className="text-gray-600 mb-4">Access platform documentation and guides.</p>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => window.open('https://emergent.sh/docs', '_blank')}>
                    View Documentation
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
            <Card className="card-base">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    User Management ({users.length} users)
                  </CardTitle>
                  <div className="flex gap-2 flex-wrap">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        placeholder="Search users..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 w-48"
                      />
                    </div>
                    <Select value={tierFilter || "all"} onValueChange={(v) => setTierFilter(v === "all" ? "" : v)}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filter by tier" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Tiers</SelectItem>
                        <SelectItem value="inner_circle">Inner Circle</SelectItem>
                        <SelectItem value="inner_edge">Inner Edge</SelectItem>
                        <SelectItem value="costa_rica">Costa Rica</SelectItem>
                        <SelectItem value="coaching_activation">Coaching</SelectItem>
                        <SelectItem value="sdc">SDC</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={() => setShowCreateUserDialog(true)} className="btn-primary">
                      <Plus className="h-4 w-4 mr-2" /> Add User
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest mx-auto"></div>
                  </div>
                ) : users.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No users found.</p>
                ) : (
                  <div className="space-y-4">
                    {users.map((u) => (
                      <div key={u.id} className={`p-4 border rounded-xl ${u.status === 'suspended' ? 'border-red-300 bg-red-50' : u.status === 'deleted' ? 'border-gray-300 bg-gray-100' : 'border-sand'}`}>
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-12 w-12">
                              <AvatarImage src={u.profile_image} />
                              <AvatarFallback className={`${u.status === 'suspended' ? 'bg-red-500' : 'bg-forest'} text-white`}>
                                {u.full_name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-forest">{u.full_name}</p>
                                {u.status === 'suspended' && <Badge className="bg-red-500 text-white text-xs">Suspended</Badge>}
                                {u.status === 'deleted' && <Badge className="bg-gray-500 text-white text-xs">Deleted</Badge>}
                                {u.role === 'admin' && <Badge className="bg-purple-600 text-white text-xs">Admin</Badge>}
                                {u.role === 'coach' && <Badge className="bg-blue-600 text-white text-xs">Coach</Badge>}
                              </div>
                              <p className="text-sm text-gray-500">{u.email}</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {u.active_tiers?.map((tier) => (
                                  <Badge 
                                    key={tier} 
                                    className={`${tierBadgeColors[tier]} text-white text-xs cursor-pointer`}
                                    onClick={() => handleRevokeTier(u.id, tier)}
                                  >
                                    {tier.replace('_', ' ')} ×
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            {/* Wallet Info */}
                            {(u.coach_sessions_remaining > 0 || u.founder_sessions_remaining > 0 || u.inner_edge_sessions_remaining > 0) && (
                              <div className="flex gap-2 text-xs">
                                {u.coach_sessions_remaining > 0 && (
                                  <span className="px-2 py-1 bg-forest/10 text-forest rounded">
                                    Coach: {u.coach_sessions_remaining}
                                  </span>
                                )}
                                {u.founder_sessions_remaining > 0 && (
                                  <span className="px-2 py-1 bg-terracotta/10 text-terracotta rounded">
                                    WT: {u.founder_sessions_remaining}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Grant Tier */}
                            <Select value={selectedUser === u.id ? selectedTier : ''} onValueChange={(v) => { setSelectedUser(u.id); setSelectedTier(v); }}>
                              <SelectTrigger className="w-32 h-8 text-xs">
                                <SelectValue placeholder="Grant tier" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="inner_circle">Inner Circle</SelectItem>
                                <SelectItem value="inner_edge">Inner Edge</SelectItem>
                                <SelectItem value="costa_rica">Costa Rica</SelectItem>
                                <SelectItem value="coaching_activation">Coaching</SelectItem>
                                <SelectItem value="sdc">SDC</SelectItem>
                              </SelectContent>
                            </Select>
                            {selectedUser === u.id && selectedTier && (
                              <Button size="sm" onClick={() => handleGrantTier(u.id)} className="h-8 btn-primary text-xs">
                                <Plus className="h-3 w-3 mr-1" /> Grant
                              </Button>
                            )}

                            {/* User Actions Button */}
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 text-xs"
                              onClick={() => { setSelectedUserForAction(u); setShowUserActionsDialog(true); }}
                            >
                              <Edit className="h-3 w-3 mr-1" /> Actions
                            </Button>
                          </div>
                        </div>

                        {/* Wallet Adjustment */}
                        {u.active_tiers?.some(t => ['coaching_activation', 'inner_edge'].includes(t)) && (
                          <div className="mt-4 pt-4 border-t border-sand">
                            <p className="text-sm font-medium text-gray-600 mb-2">Adjust Wallet:</p>
                            <div className="flex flex-wrap gap-2">
                              <Select 
                                value={selectedUser === u.id ? walletAdjustment.type : ''} 
                                onValueChange={(v) => { setSelectedUser(u.id); setWalletAdjustment(prev => ({ ...prev, type: v })); }}
                              >
                                <SelectTrigger className="w-32 h-8 text-xs">
                                  <SelectValue placeholder="Wallet" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="coach_sessions">Coach</SelectItem>
                                  <SelectItem value="founder_sessions">Wonder Twin</SelectItem>
                                  <SelectItem value="inner_edge_sessions">Inner Edge</SelectItem>
                                </SelectContent>
                              </Select>
                              <Input 
                                type="number" 
                                placeholder="+/- amount"
                                className="w-20 h-8 text-xs"
                                value={selectedUser === u.id ? walletAdjustment.amount : ''}
                                onChange={(e) => { setSelectedUser(u.id); setWalletAdjustment(prev => ({ ...prev, amount: e.target.value })); }}
                              />
                              <Input 
                                placeholder="Reason"
                                className="w-32 h-8 text-xs"
                                value={selectedUser === u.id ? walletAdjustment.reason : ''}
                                onChange={(e) => { setSelectedUser(u.id); setWalletAdjustment(prev => ({ ...prev, reason: e.target.value })); }}
                              />
                              {selectedUser === u.id && walletAdjustment.type && (
                                <Button size="sm" onClick={() => handleWalletAdjust(u.id)} className="h-8 btn-outline text-xs">
                                  <Wallet className="h-3 w-3 mr-1" /> Adjust
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
        )}

        {/* Create User Dialog */}
        <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest">Add New User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Full Name *</Label>
                <Input 
                  value={createUserForm.full_name}
                  onChange={(e) => setCreateUserForm(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input 
                  type="email"
                  value={createUserForm.email}
                  onChange={(e) => setCreateUserForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="jane@example.com"
                />
              </div>
              <div>
                <Label>Password (leave blank to auto-generate)</Label>
                <Input 
                  type="password"
                  value={createUserForm.password}
                  onChange={(e) => setCreateUserForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Auto-generated if empty"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={createUserForm.role} onValueChange={(v) => setCreateUserForm(prev => ({ ...prev, role: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client/Member</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="send_welcome"
                  checked={createUserForm.send_welcome_email}
                  onChange={(e) => setCreateUserForm(prev => ({ ...prev, send_welcome_email: e.target.checked }))}
                />
                <Label htmlFor="send_welcome">Send welcome email with login details</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateUserDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateUser} className="btn-primary">Create User</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* User Actions Dialog */}
        <Dialog open={showUserActionsDialog} onOpenChange={setShowUserActionsDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest">
                User Actions: {selectedUserForAction?.full_name}
              </DialogTitle>
              <DialogDescription>{selectedUserForAction?.email}</DialogDescription>
            </DialogHeader>
            
            {selectedUserForAction && (
              <div className="space-y-4 py-4">
                {/* User Status */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>Status:</strong>{' '}
                    <span className={`${selectedUserForAction.status === 'suspended' ? 'text-red-600' : selectedUserForAction.status === 'deleted' ? 'text-gray-600' : 'text-green-600'}`}>
                      {selectedUserForAction.status || 'Active'}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600"><strong>Role:</strong> {selectedUserForAction.role}</p>
                  <p className="text-sm text-gray-600"><strong>Joined:</strong> {selectedUserForAction.created_at?.slice(0, 10)}</p>
                </div>

                {/* View Activity Timeline */}
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-forest border-forest hover:bg-forest/10"
                  onClick={() => fetchUserActivity(selectedUserForAction.id)}
                  disabled={loadingActivity}
                >
                  <BarChart3 className="h-4 w-4 mr-2" /> 
                  {loadingActivity ? 'Loading...' : 'View Activity Timeline'}
                </Button>

                {/* Reset Password */}
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => handleResetPassword(selectedUserForAction.id)}
                >
                  <Shield className="h-4 w-4 mr-2" /> Reset Password
                </Button>

                {/* Suspend/Reactivate */}
                {selectedUserForAction.status === 'suspended' ? (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-green-600 border-green-600 hover:bg-green-50"
                    onClick={() => handleReactivateUser(selectedUserForAction.id)}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Reactivate User
                  </Button>
                ) : selectedUserForAction.role !== 'admin' && (
                  <div className="space-y-2">
                    <Input 
                      placeholder="Reason for suspension (optional)"
                      value={suspendReason}
                      onChange={(e) => setSuspendReason(e.target.value)}
                    />
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-orange-600 border-orange-600 hover:bg-orange-50"
                      onClick={() => handleSuspendUser(selectedUserForAction.id)}
                    >
                      <XCircle className="h-4 w-4 mr-2" /> Suspend User
                    </Button>
                  </div>
                )}

                {/* Delete Options */}
                {selectedUserForAction.role !== 'admin' && (
                  <div className="pt-4 border-t space-y-2">
                    <p className="text-sm font-medium text-red-600">Danger Zone</p>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-red-600 border-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteUser(selectedUserForAction.id, false)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Soft Delete (Recoverable)
                    </Button>
                    <Button 
                      variant="destructive" 
                      className="w-full justify-start"
                      onClick={() => handleDeleteUser(selectedUserForAction.id, true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Permanently Delete
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* User Activity Timeline Dialog */}
        <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Activity Timeline
              </DialogTitle>
              {userActivity?.user && (
                <DialogDescription className="flex items-center gap-3 pt-2">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={userActivity.user.profile_image} />
                    <AvatarFallback className="bg-forest/10 text-forest">
                      {userActivity.user.full_name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-forest">{userActivity.user.full_name}</p>
                    <p className="text-sm text-gray-500">{userActivity.user.email}</p>
                  </div>
                </DialogDescription>
              )}
            </DialogHeader>
            
            {userActivity && (
              <div className="space-y-6 py-4">
                {/* Engagement Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{userActivity.stats.total_posts}</p>
                    <p className="text-xs text-blue-600/70">Posts</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{userActivity.stats.total_journal_entries}</p>
                    <p className="text-xs text-green-600/70">Journal Entries</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-terracotta">{userActivity.stats.total_bookings}</p>
                    <p className="text-xs text-terracotta/70">Sessions Booked</p>
                  </div>
                  <div className="bg-pink-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-pink-600">{userActivity.stats.diamond_points}</p>
                    <p className="text-xs text-pink-600/70">Diamond Points</p>
                  </div>
                </div>

                {/* Member Info */}
                <div className="flex flex-wrap gap-2 text-sm">
                  <Badge variant="outline" className="bg-gray-50">
                    Member since {userActivity.stats.member_since?.slice(0, 10)}
                  </Badge>
                  {userActivity.stats.active_tiers?.map(tier => (
                    <Badge key={tier} className="bg-forest/10 text-forest">
                      {tier.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                  <Badge className={userActivity.stats.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                    {userActivity.stats.status || 'Active'}
                  </Badge>
                </div>

                {/* Activity Timeline */}
                <div className="space-y-2">
                  <h3 className="font-medium text-forest text-sm">Recent Activity</h3>
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                    
                    {/* Activities */}
                    <div className="space-y-3">
                      {userActivity.activities?.map((activity, idx) => (
                        <div key={idx} className="flex gap-3 relative">
                          {/* Icon circle */}
                          <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${getActivityColor(activity.type)}`}>
                            {getActivityIcon(activity.icon)}
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0 pb-3">
                            <p className="text-sm text-gray-900">{activity.description}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <p className="text-xs text-gray-500">
                                {activity.timestamp ? new Date(activity.timestamp).toLocaleDateString('en-US', { 
                                  month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                }) : 'Unknown date'}
                              </p>
                              {activity.by && (
                                <span className="text-xs text-purple-600">by {activity.by}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {userActivity.activities?.length === 0 && (
                        <p className="text-sm text-gray-500 py-4 text-center">No activity recorded yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

          {/* Communities Tab */}
          {activeTab === 'communities' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="font-heading text-xl text-forest">Community Rooms</h2>
              </div>
              
              {/* Community Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {adminCommunities.map((community) => (
                  <Card key={community.id} className="card-base">
                    <div className="relative h-32 overflow-hidden rounded-t-xl">
                      <img 
                        src={community.image_url} 
                        alt={community.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2">
                        <Badge className="bg-white/90 text-forest text-xs">{community.category}</Badge>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-forest mb-1 line-clamp-1">{community.name}</h3>
                      <p className="text-xs text-gray-500 mb-2 line-clamp-2">{community.description}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                        <span>{community.member_count || 0} members</span>
                        <span>{community.post_count || 0} posts</span>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1 text-xs"
                          onClick={() => setEditingCommunity(community)}
                        >
                          <Edit className="h-3 w-3 mr-1" /> Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1 text-xs"
                          onClick={() => {
                            setSelectedCommunityForPosts(community);
                            fetchCommunityPosts(community.id);
                          }}
                        >
                          <MessageSquare className="h-3 w-3 mr-1" /> Moderate
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Edit Community Dialog */}
              <Dialog open={!!editingCommunity} onOpenChange={() => setEditingCommunity(null)}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Edit Community: {editingCommunity?.name}</DialogTitle>
                  </DialogHeader>
                  {editingCommunity && (
                    <div className="space-y-4">
                      <div>
                        <Label>Name</Label>
                        <Input 
                          value={editingCommunity.name}
                          onChange={(e) => setEditingCommunity({...editingCommunity, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea 
                          value={editingCommunity.description}
                          onChange={(e) => setEditingCommunity({...editingCommunity, description: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Image URL</Label>
                        <Input 
                          value={editingCommunity.image_url}
                          onChange={(e) => setEditingCommunity({...editingCommunity, image_url: e.target.value})}
                          placeholder="https://..."
                        />
                        {editingCommunity.image_url && (
                          <img 
                            src={editingCommunity.image_url} 
                            alt="Preview" 
                            className="mt-2 h-24 w-full object-cover rounded-lg"
                          />
                        )}
                      </div>
                      <div>
                        <Label>Guidelines</Label>
                        <Textarea 
                          value={editingCommunity.guidelines || ''}
                          onChange={(e) => setEditingCommunity({...editingCommunity, guidelines: e.target.value})}
                          placeholder="Community guidelines..."
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => setEditingCommunity(null)}>Cancel</Button>
                        <Button 
                          className="btn-primary"
                          onClick={() => handleUpdateCommunity(editingCommunity.id, {
                            name: editingCommunity.name,
                            description: editingCommunity.description,
                            image_url: editingCommunity.image_url,
                            guidelines: editingCommunity.guidelines
                          })}
                        >
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* Moderate Posts Dialog */}
              <Dialog open={!!selectedCommunityForPosts} onOpenChange={() => setSelectedCommunityForPosts(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Moderate: {selectedCommunityForPosts?.name}</DialogTitle>
                    <DialogDescription>Review and manage community posts</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    {communityPosts.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No posts in this community yet.</p>
                    ) : (
                      communityPosts.map((post) => (
                        <div key={post.id} className={`p-4 border rounded-lg ${post.is_flagged ? 'border-red-300 bg-red-50' : 'border-sand'}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="h-8 w-8 rounded-full bg-forest/10 flex items-center justify-center">
                                <span className="text-xs font-semibold text-forest">{post.author_name?.charAt(0)}</span>
                              </div>
                              <div>
                                <p className="text-sm font-semibold">{post.author_name}</p>
                                <p className="text-xs text-gray-400">{new Date(post.created_at).toLocaleString()}</p>
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeletePost(selectedCommunityForPosts.id, post.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-gray-700">{post.content}</p>
                          {post.is_flagged && (
                            <Badge className="mt-2 bg-red-100 text-red-700">Flagged: {post.flag_reason}</Badge>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="space-y-6">
              {/* Weekly Soul Food Section */}
              <AdminScriptures />
              
              {/* Weekly Gems and Reflections Grid */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Weekly Gems */}
                <Card className="card-base">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                        <Radio className="h-5 w-5 text-terracotta" />
                        Weekly Gems
                      </CardTitle>
                      <Button 
                        onClick={() => setShowTransmissionForm(!showTransmissionForm)}
                        className="btn-primary h-8 text-xs"
                        data-testid="new-transmission-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" /> New
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* New Gem Form */}
                  {showTransmissionForm && (
                    <div className="p-4 bg-sand/30 rounded-xl mb-4 space-y-3" data-testid="transmission-form">
                      <div>
                        <Label className="text-sm font-medium text-forest">Title</Label>
                        <Input 
                          placeholder="e.g., Embracing Your Power This Week"
                          value={newTransmission.title}
                          onChange={(e) => setNewTransmission(prev => ({ ...prev, title: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-forest">Message Content</Label>
                        <Textarea 
                          placeholder="Write your weekly transmission message to your Inner Circle..."
                          value={newTransmission.content}
                          onChange={(e) => setNewTransmission(prev => ({ ...prev, content: e.target.value }))}
                          className="mt-1 min-h-[120px]"
                        />
                      </div>
                      
                      {/* Media Upload Section */}
                      <div>
                        <Label className="text-sm font-medium text-forest mb-2 block">Attach Media (optional)</Label>
                        
                        {/* Hidden file inputs */}
                        <input
                          type="file"
                          ref={audioInputRef}
                          onChange={(e) => handleMediaUpload(e, 'audio')}
                          accept=".mp3,.wav,.m4a,.aac,.ogg,.flac"
                          className="hidden"
                        />
                        <input
                          type="file"
                          ref={videoInputRef}
                          onChange={(e) => handleMediaUpload(e, 'video')}
                          accept=".mp4,.mov,.webm,.avi,.mkv"
                          className="hidden"
                        />
                        
                        {/* Upload progress */}
                        {uploadingMedia && (
                          <div className="mb-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm text-gray-600">Uploading...</span>
                              <span className="text-sm font-medium text-terracotta">{uploadProgress}%</span>
                            </div>
                            <Progress value={uploadProgress} className="h-2" />
                          </div>
                        )}
                        
                        {/* Show uploaded media or upload buttons */}
                        {newTransmission.media_type ? (
                          <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-sand">
                            {newTransmission.media_type === 'audio' && (
                              <>
                                <Music className="h-5 w-5 text-terracotta" />
                                <span className="text-sm text-gray-700 flex-1">Audio attached</span>
                                <audio controls className="h-8 max-w-[200px]">
                                  <source src={`${API_URL}${newTransmission.audio_url}`} />
                                </audio>
                              </>
                            )}
                            {newTransmission.media_type === 'video' && (
                              <>
                                <Video className="h-5 w-5 text-terracotta" />
                                <span className="text-sm text-gray-700 flex-1">Video file attached</span>
                              </>
                            )}
                            {newTransmission.media_type === 'vimeo' && (
                              <>
                                <Link2 className="h-5 w-5 text-terracotta" />
                                <span className="text-sm text-gray-700 flex-1 truncate">Vimeo: {newTransmission.vimeo_url}</span>
                              </>
                            )}
                            {newTransmission.media_type === 'youtube' && (
                              <>
                                <Video className="h-5 w-5 text-red-500" />
                                <span className="text-sm text-gray-700 flex-1 truncate">YouTube: {newTransmission.youtube_url}</span>
                              </>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={clearMedia}
                              className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {/* Upload buttons */}
                            <div className="flex gap-2 flex-wrap">
                              <Button 
                                type="button"
                                variant="outline" 
                                size="sm"
                                onClick={() => audioInputRef.current?.click()}
                                disabled={uploadingMedia}
                                className="text-sm"
                              >
                                <Music className="h-4 w-4 mr-2" /> Upload Audio
                              </Button>
                              <Button 
                                type="button"
                                variant="outline" 
                                size="sm"
                                onClick={() => videoInputRef.current?.click()}
                                disabled={uploadingMedia}
                                className="text-sm"
                              >
                                <Video className="h-4 w-4 mr-2" /> Upload Video
                              </Button>
                            </div>
                            
                            {/* Vimeo URL input */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">or</span>
                              <div className="flex-1 relative">
                                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input 
                                  placeholder="Paste Vimeo URL (e.g., https://vimeo.com/123456789)"
                                  value={newTransmission.vimeo_url}
                                  onChange={(e) => handleVimeoUrlChange(e.target.value)}
                                  className="pl-9 text-sm"
                                />
                              </div>
                            </div>
                            
                            {/* YouTube URL input */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">or</span>
                              <div className="flex-1 relative">
                                <Video className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
                                <Input 
                                  placeholder="Paste YouTube URL (e.g., https://youtube.com/watch?v=...)"
                                  value={newTransmission.youtube_url}
                                  onChange={(e) => handleYoutubeUrlChange(e.target.value)}
                                  className="pl-9 text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          Upload: Audio (MP3, WAV) or Video (MP4, MOV) up to 100MB • Or paste a Vimeo/YouTube URL
                        </p>
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <Button onClick={handleCreateTransmission} className="btn-secondary text-sm" data-testid="publish-transmission-btn" disabled={uploadingMedia}>
                          <Send className="h-4 w-4 mr-2" /> Publish
                        </Button>
                        <Button variant="outline" onClick={() => { setShowTransmissionForm(false); clearMedia(); }} className="text-sm">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Gems List */}
                  {transmissions.length === 0 ? (
                    <p className="text-gray-500 text-center py-6">No transmissions yet. Create your first one!</p>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {transmissions.map((t) => (
                        <div key={t.id} className="p-4 border border-sand rounded-xl hover:border-terracotta/30 transition-colors" data-testid={`transmission-${t.id}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h4 className="font-medium text-forest">{t.title}</h4>
                              <p className="text-xs text-gray-400 mt-0.5">{formatDate(t.created_at)}</p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteTransmission(t.id)}
                              className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-3">{t.content}</p>
                          {t.media_type === 'audio' && t.audio_url && (
                            <div className="mt-2 flex items-center gap-2">
                              <Music className="h-4 w-4 text-terracotta" />
                              <span className="text-xs text-terracotta">Audio attached</span>
                            </div>
                          )}
                          {t.media_type === 'video' && t.video_url && (
                            <div className="mt-2 flex items-center gap-2">
                              <Video className="h-4 w-4 text-terracotta" />
                              <span className="text-xs text-terracotta">Video attached</span>
                            </div>
                          )}
                          {t.media_type === 'vimeo' && t.vimeo_url && (
                            <div className="mt-2 flex items-center gap-2">
                              <Link2 className="h-4 w-4 text-terracotta" />
                              <span className="text-xs text-terracotta">Vimeo video</span>
                            </div>
                          )}
                          {t.media_type === 'youtube' && t.youtube_url && (
                            <div className="mt-2 flex items-center gap-2">
                              <Video className="h-4 w-4 text-red-500" />
                              <span className="text-xs text-red-500">YouTube video</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Weekly Reflection Prompts */}
              <Card className="card-base">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                      <FileText className="h-5 w-5 text-terracotta" />
                      Reflection Prompts
                    </CardTitle>
                    <Button 
                      onClick={() => setShowPromptForm(!showPromptForm)}
                      className="btn-primary h-8 text-xs"
                      data-testid="new-prompt-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" /> New
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* New Prompt Form */}
                  {showPromptForm && (
                    <div className="p-4 bg-sand/30 rounded-xl mb-4 space-y-3" data-testid="prompt-form">
                      <div>
                        <Label className="text-sm font-medium text-forest">Title</Label>
                        <Input 
                          placeholder="e.g., Weekly Reflection: Boundaries"
                          value={newPrompt.title}
                          onChange={(e) => setNewPrompt(prev => ({ ...prev, title: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-forest">Prompt Question</Label>
                        <Textarea 
                          placeholder="Write a reflective question for your community..."
                          value={newPrompt.prompt_text}
                          onChange={(e) => setNewPrompt(prev => ({ ...prev, prompt_text: e.target.value }))}
                          className="mt-1 min-h-[100px]"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button onClick={handleCreatePrompt} className="btn-secondary text-sm" data-testid="publish-prompt-btn">
                          <Send className="h-4 w-4 mr-2" /> Publish
                        </Button>
                        <Button variant="outline" onClick={() => setShowPromptForm(false)} className="text-sm">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Prompts List */}
                  {prompts.length === 0 ? (
                    <p className="text-gray-500 text-center py-6">No prompts yet. Create your first one!</p>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {prompts.map((p) => (
                        <div key={p.id} className="p-4 border border-sand rounded-xl hover:border-terracotta/30 transition-colors" data-testid={`prompt-${p.id}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h4 className="font-medium text-forest">{p.title}</h4>
                              <p className="text-xs text-gray-400 mt-0.5">{formatDate(p.created_at)}</p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeletePrompt(p.id)}
                              className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-gray-600 italic">&ldquo;{p.prompt_text}&rdquo;</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              </div>
            </div>
          )}

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest">Session Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No bookings yet.</p>
                ) : (
                  <div className="space-y-4">
                    {bookings.map((booking) => (
                      <div key={booking.id} className="flex items-center justify-between p-4 bg-sand/30 rounded-xl">
                        <div>
                          <p className="font-medium text-forest">
                            {booking.coach_name} - {booking.session_type === 'wonder_twin' ? 'Wonder Twin' : 'Coach Session'}
                          </p>
                          <p className="text-sm text-gray-500">User ID: {booking.user_id.slice(0, 8)}...</p>
                          <p className="text-xs text-gray-400">{new Date(booking.created_at).toLocaleString()}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={
                            booking.status === 'completed' ? 'bg-green-100 text-green-700' :
                            booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {booking.status}
                          </Badge>
                          {booking.status === 'pending' && (
                            <Button 
                              size="sm" 
                              onClick={() => handleCompleteSession(booking.id)}
                              className="btn-primary text-xs"
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Hot Seat Requests */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Flame className="h-5 w-5 text-terracotta" />
                    Hot Seat Requests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {hotSeats.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No hot seat requests.</p>
                  ) : (
                    <div className="space-y-4">
                      {hotSeats.map((req) => (
                        <div key={req.id} className="p-4 bg-sand/30 rounded-xl">
                          <div className="flex justify-between mb-2">
                            <p className="font-medium text-forest">{req.topic}</p>
                            <Badge className={
                              req.status === 'approved' ? 'bg-green-100 text-green-700' :
                              'bg-yellow-100 text-yellow-700'
                            }>
                              {req.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{req.description}</p>
                          <p className="text-xs text-gray-400">From: {req.user_name}</p>
                          {req.status === 'pending' && (
                            <div className="flex gap-2 mt-3">
                              <Button size="sm" onClick={() => handleUpdateRequest('hot-seat', req.id, 'approved')} className="btn-primary text-xs">
                                Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleUpdateRequest('hot-seat', req.id, 'declined')} className="text-xs">
                                Decline
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Concierge Requests */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-terracotta" />
                    Concierge Requests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {concierge.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No concierge requests.</p>
                  ) : (
                    <div className="space-y-4">
                      {concierge.map((req) => (
                        <div key={req.id} className="p-4 bg-sand/30 rounded-xl">
                          <div className="flex justify-between mb-2">
                            <p className="font-medium text-forest capitalize">{req.request_type}</p>
                            <Badge className={
                              req.status === 'completed' ? 'bg-green-100 text-green-700' :
                              'bg-yellow-100 text-yellow-700'
                            }>
                              {req.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{req.description}</p>
                          <p className="text-xs text-gray-400">From: {req.user_name} | Urgency: {req.urgency}</p>
                          {req.status === 'pending' && (
                            <div className="flex gap-2 mt-3">
                              <Button size="sm" onClick={() => handleUpdateRequest('concierge', req.id, 'completed')} className="btn-primary text-xs">
                                Complete
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Coach Notes Tab */}
          {activeTab === 'coach-notes' && (
            <Card className="card-base">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Coach Confidential Notes
                    </CardTitle>
                    <p className="text-sm text-gray-500">View all confidential notes from coaches about their clients</p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-gray-500">Sort by:</span>
                    <Select value={notesSortBy} onValueChange={setNotesSortBy}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date">Date</SelectItem>
                        <SelectItem value="coach">Coach Name</SelectItem>
                        <SelectItem value="client">Client Name</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search and Filter Bar */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="grid sm:grid-cols-3 gap-4">
                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search notes..."
                        value={notesSearchTerm}
                        onChange={(e) => setNotesSearchTerm(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    
                    {/* Coach Filter */}
                    <Select value={notesCoachFilter} onValueChange={setNotesCoachFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by Coach" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Coaches</SelectItem>
                        {[...new Set(coachNotes.map(n => n.coach_name).filter(Boolean))].map(coach => (
                          <SelectItem key={coach} value={coach}>{coach}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Client Filter */}
                    <Select value={notesClientFilter} onValueChange={setNotesClientFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by Client" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Clients</SelectItem>
                        {[...new Set(coachNotes.map(n => n.client_name).filter(Boolean))].map(client => (
                          <SelectItem key={client} value={client}>{client}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Clear Filters */}
                  {(notesSearchTerm || (notesCoachFilter && notesCoachFilter !== 'all') || (notesClientFilter && notesClientFilter !== 'all')) && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="mt-2 text-terracotta"
                      onClick={() => {
                        setNotesSearchTerm('');
                        setNotesCoachFilter('');
                        setNotesClientFilter('');
                      }}
                    >
                      Clear all filters
                    </Button>
                  )}
                </div>

                {coachNotes.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No coach notes found.</p>
                ) : (
                  <div className="space-y-4">
                    {[...coachNotes]
                      .filter(note => {
                        // Search filter
                        if (notesSearchTerm) {
                          const search = notesSearchTerm.toLowerCase();
                          const matchesSearch = 
                            note.note?.toLowerCase().includes(search) ||
                            note.client_name?.toLowerCase().includes(search) ||
                            note.coach_name?.toLowerCase().includes(search) ||
                            note.client_email?.toLowerCase().includes(search);
                          if (!matchesSearch) return false;
                        }
                        // Coach filter
                        if (notesCoachFilter && notesCoachFilter !== 'all' && note.coach_name !== notesCoachFilter) {
                          return false;
                        }
                        // Client filter
                        if (notesClientFilter && notesClientFilter !== 'all' && note.client_name !== notesClientFilter) {
                          return false;
                        }
                        return true;
                      })
                      .sort((a, b) => {
                        if (notesSortBy === 'coach') return (a.coach_name || '').localeCompare(b.coach_name || '');
                        if (notesSortBy === 'client') return (a.client_name || '').localeCompare(b.client_name || '');
                        return new Date(b.created_at) - new Date(a.created_at);
                      })
                      .map((note) => (
                      <div key={note.id} className="p-4 border border-sand rounded-xl">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-forest">Re: {note.client_name}</p>
                            <p className="text-xs text-gray-500">{note.client_email}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline" className="text-xs mb-1">{note.note_type}</Badge>
                            <p className="text-xs text-gray-400">{formatDate(note.created_at)}</p>
                          </div>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap mb-3 bg-gray-50 p-3 rounded-lg">{note.note}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="font-medium">Coach:</span>
                          <span>{note.coach_name}</span>
                          <span className="text-gray-300">|</span>
                          <span>{note.coach_email}</span>
                        </div>
                      </div>
                    ))}
                    
                    {/* No results message */}
                    {coachNotes.length > 0 && [...coachNotes].filter(note => {
                      if (notesSearchTerm) {
                        const search = notesSearchTerm.toLowerCase();
                        const matchesSearch = 
                          note.note?.toLowerCase().includes(search) ||
                          note.client_name?.toLowerCase().includes(search) ||
                          note.coach_name?.toLowerCase().includes(search);
                        if (!matchesSearch) return false;
                      }
                      if (notesCoachFilter && notesCoachFilter !== 'all' && note.coach_name !== notesCoachFilter) return false;
                      if (notesClientFilter && notesClientFilter !== 'all' && note.client_name !== notesClientFilter) return false;
                      return true;
                    }).length === 0 && (
                      <p className="text-gray-500 text-center py-8">No notes match your filters.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Coach Invoices Tab */}
          {activeTab === 'coach-invoices' && (
            <Card className="card-base">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                      <Wallet className="h-5 w-5" />
                      Coach Invoices
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">Review and process coach invoices for payment</p>
                  </div>
                  <div className="flex gap-3 items-center">
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-500">Filter:</span>
                      <Select value={invoiceStatusFilter || "all"} onValueChange={(v) => setInvoiceStatusFilter(v === "all" ? "" : v)}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="returned">Returned</SelectItem>
                          <SelectItem value="rescinded">Rescinded</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-500">Sort:</span>
                      <Select value={invoiceSortBy} onValueChange={setInvoiceSortBy}>
                        <SelectTrigger className="w-36">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="date">Newest First</SelectItem>
                          <SelectItem value="status">By Status</SelectItem>
                          <SelectItem value="due">Due Soonest</SelectItem>
                          <SelectItem value="amount">Highest Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {coachInvoices.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No invoices found.</p>
                ) : (
                  <div className="space-y-4">
                    {sortInvoices(coachInvoices).map((invoice) => {
                      const dueInfo = getInvoiceDueInfo(invoice.created_at);
                      const showDueWarning = ['pending', 'approved'].includes(invoice.status);
                      
                      return (
                      <div 
                        key={invoice.id} 
                        className={`p-4 border rounded-xl ${
                          showDueWarning && dueInfo.isOverdue 
                            ? 'border-red-300 bg-red-50/50' 
                            : showDueWarning && dueInfo.daysUntilDue <= 7 
                              ? 'border-orange-300 bg-orange-50/50' 
                              : 'border-sand'
                        }`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <p className="font-semibold text-forest">Invoice #{invoice.invoice_number}</p>
                              <Badge className={
                                invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                                invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                invoice.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                                invoice.status === 'returned' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }>
                                {invoice.status}
                              </Badge>
                              {/* Days Until Due Badge */}
                              {showDueWarning && (
                                <Badge className={
                                  dueInfo.isOverdue 
                                    ? 'bg-red-500 text-white' 
                                    : dueInfo.daysUntilDue <= 7 
                                      ? 'bg-orange-500 text-white' 
                                      : dueInfo.daysUntilDue <= 30 
                                        ? 'bg-yellow-500 text-white' 
                                        : 'bg-gray-200 text-gray-700'
                                }>
                                  {dueInfo.isOverdue 
                                    ? `OVERDUE by ${Math.abs(dueInfo.daysUntilDue)} days` 
                                    : `${dueInfo.daysUntilDue} days until due`}
                                </Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                              <div>
                                <p className="text-gray-500">Coach:</p>
                                <p className="font-medium">{invoice.coach_name}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Client:</p>
                                <p className="font-medium">{invoice.client_name}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Sessions:</p>
                                <p className="font-medium">{invoice.sessions_count}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Dates:</p>
                                <p className="font-medium">{invoice.session_dates?.join(', ') || 'N/A'}</p>
                              </div>
                            </div>
                            {invoice.notes && (
                              <p className="text-sm text-gray-600 mb-2"><strong>Notes:</strong> {invoice.notes}</p>
                            )}
                            {invoice.attachment_url && (
                              <a 
                                href={`${API_URL}${invoice.attachment_url.replace("/api/uploads/invoices/", "/api/invoice-attachments/")}?auth=${localStorage.getItem("token")}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                              >
                                <Link2 className="h-3 w-3" /> View Attachment
                              </a>
                            )}
                            {invoice.admin_comments && (
                              <p className="text-sm text-orange-600 mt-2"><strong>Admin Comments:</strong> {invoice.admin_comments}</p>
                            )}
                            {invoice.payment_details && (
                              <p className="text-sm text-green-600 mt-1"><strong>Payment:</strong> {invoice.payment_details}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-forest">${invoice.amount}</p>
                            <p className="text-xs text-gray-500">Created: {formatDate(invoice.created_at)}</p>
                            {showDueWarning && (
                              <p className={`text-xs mb-3 ${dueInfo.isOverdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                Due: {dueInfo.dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            )}
                            {!showDueWarning && <div className="mb-3" />}
                            
                            {/* Action Buttons */}
                            {invoice.status === 'pending' && (
                              <div className="flex flex-col gap-2">
                                <Button 
                                  size="sm" 
                                  className="btn-primary text-xs"
                                  onClick={() => handleUpdateInvoice(invoice.id, { status: 'approved' })}
                                >
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-xs text-red-600 border-red-300"
                                  onClick={() => {
                                    const comment = prompt('Enter reason for return:');
                                    if (comment) handleUpdateInvoice(invoice.id, { status: 'returned', admin_comments: comment });
                                  }}
                                >
                                  <XCircle className="h-3 w-3 mr-1" /> Return
                                </Button>
                              </div>
                            )}
                            {invoice.status === 'approved' && (
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                onClick={() => {
                                  const payment = prompt('Enter payment details (e.g., Check #, Date):');
                                  if (payment) handleUpdateInvoice(invoice.id, { status: 'paid', payment_details: payment });
                                }}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Mark Paid
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="flex h-[calc(100vh-16rem)] border border-sand rounded-lg overflow-hidden bg-white">
              {/* Conversations List */}
              <div className={`w-full md:w-80 border-r border-sand flex flex-col ${selectedPartner ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-4 border-b border-sand">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="font-heading text-xl font-semibold text-forest">Messages</h2>
                  </div>
                  {/* New Message Button */}
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="btn-primary w-full" size="sm">
                        <Plus className="h-4 w-4 mr-2" /> New Message
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="font-heading">Start New Conversation</DialogTitle>
                        <DialogDescription>Select any user to message</DialogDescription>
                      </DialogHeader>
                      <div className="py-4">
                        <Input 
                          placeholder="Search users..." 
                          className="mb-4"
                          onChange={(e) => {
                            const search = e.target.value.toLowerCase();
                            // Filter is applied in the map below
                          }}
                        />
                        <div className="max-h-60 overflow-y-auto space-y-2">
                          {allUsers.filter(u => u.id !== user?.id).map((u) => (
                            <Button
                              key={u.id}
                              variant="outline"
                              className="w-full justify-start"
                              onClick={() => {
                                setSelectedPartner(u);
                                fetchMessagesWithUser(u.id);
                              }}
                            >
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 ${
                                u.is_coach ? 'bg-terracotta/10' : 'bg-forest/10'
                              }`}>
                                <User className={`h-4 w-4 ${u.is_coach ? 'text-terracotta' : 'text-forest'}`} />
                              </div>
                              <div className="text-left">
                                <p className="font-medium">{u.full_name}</p>
                                <p className="text-xs text-gray-500">{u.email} {u.is_coach && '(Coach)'}</p>
                              </div>
                            </Button>
                          ))}
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {conversations.length === 0 ? (
                    <div className="p-8 text-center">
                      <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No conversations yet</p>
                      <p className="text-sm text-gray-400 mt-2">Click "New Message" to start</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-sand">
                      {conversations.map((conv) => (
                        <button 
                          key={conv.partner?.id}
                          onClick={() => {
                            setSelectedPartner(conv.partner);
                            fetchMessagesWithUser(conv.partner?.id);
                          }}
                          className={`w-full flex items-center gap-3 p-4 hover:bg-sand/50 transition-colors text-left ${
                            selectedPartner?.id === conv.partner?.id ? 'bg-sand' : ''
                          }`}
                        >
                          <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                            conv.partner?.type === 'coach' ? 'bg-terracotta/10' : 'bg-forest/10'
                          }`}>
                            <User className={`h-6 w-6 ${conv.partner?.type === 'coach' ? 'text-terracotta' : 'text-forest'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h3 className="font-medium text-forest truncate">{conv.partner?.full_name}</h3>
                              {conv.last_message && (
                                <span className="text-xs text-gray-400">
                                  {formatMessageTime(conv.last_message.created_at)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`text-xs ${
                                conv.partner?.type === 'coach' ? 'border-terracotta/50 text-terracotta' : ''
                              }`}>
                                {conv.partner?.type === 'coach' ? 'Coach' : 'Client'}
                              </Badge>
                              {conv.last_message && (
                                <p className="text-sm text-gray-500 truncate flex-1">{conv.last_message.content}</p>
                              )}
                            </div>
                          </div>
                          {conv.unread_count > 0 && (
                            <span className="h-5 w-5 rounded-full bg-terracotta text-white text-xs flex items-center justify-center">
                              {conv.unread_count}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Area */}
              <div className={`flex-1 flex flex-col bg-cream/50 ${!selectedPartner ? 'hidden md:flex' : 'flex'}`}>
                {selectedPartner ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-sand bg-white flex items-center gap-3">
                      <button onClick={() => setSelectedPartner(null)} className="md:hidden">
                        <XCircle className="h-5 w-5" />
                      </button>
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                        selectedPartner.type === 'coach' || selectedPartner.is_coach ? 'bg-terracotta/10' : 'bg-forest/10'
                      }`}>
                        <User className={`h-5 w-5 ${selectedPartner.type === 'coach' || selectedPartner.is_coach ? 'text-terracotta' : 'text-forest'}`} />
                      </div>
                      <div>
                        <h2 className="font-medium text-forest">{selectedPartner.full_name}</h2>
                        <p className="text-sm text-gray-500">{selectedPartner.email}</p>
                      </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4">
                      <div className="space-y-4 max-w-3xl mx-auto">
                        {messages.length === 0 ? (
                          <div className="text-center py-8">
                            <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">No messages yet</p>
                            <p className="text-sm text-gray-400">Send a message to start the conversation</p>
                          </div>
                        ) : (
                          messages.map((msg) => {
                            const isSent = msg.sender_id === user?.id;
                            return (
                              <div key={msg.id} className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[70%] rounded-xl px-4 py-2 ${
                                  isSent 
                                    ? 'bg-forest text-white rounded-br-sm' 
                                    : 'bg-white border border-sand text-gray-800 rounded-bl-sm'
                                }`}>
                                  <p>{msg.content}</p>
                                  <span className={`text-xs mt-1 block ${isSent ? 'text-white/70' : 'text-gray-500'}`}>
                                    {formatMessageTime(msg.created_at)}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    </div>

                    {/* Message Input */}
                    <div className="p-4 border-t border-sand bg-white">
                      <form onSubmit={handleSendAdminMessage} className="flex gap-3 max-w-3xl mx-auto">
                        <Input
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          className="input-base flex-1"
                        />
                        <Button 
                          type="submit" 
                          className="btn-primary"
                          disabled={!newMessage.trim() || sendingMessage}
                        >
                          <Send className="h-5 w-5" />
                        </Button>
                      </form>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="font-heading font-semibold text-forest mb-2">Select a Conversation</h3>
                      <p className="text-gray-500">Choose a user to start messaging</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Safety & Moderation Tab */}
          {activeTab === 'safety' && (
            <AdminSafetyDashboard />
          )}

          {/* E-Signatures Tab */}
          {activeTab === 'esign' && (
            <AdminESignatures />
          )}

      </div>
    </DashboardLayout>
  );
};

export default AdminPage;
